/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, MenuItem, TextField } from "@mui/material";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import validate from "validate.js";
import {
  addCommonResource,
  resetStateCommon,
  showSnackbar,
  updateCommonResource,
} from "../../app/slices/commonSlice";
import { getBase64, hasAccess, getObjectDifference } from "../../app/utils";
import FileInput from "../../components/fileInput/fileInput";

const UpdateGenreDialog = ({ open, handleClose, selectedGenre }) => {
  const {
    put: { data: dataPutGenre, status: statusPutGenre, error: errorPutGenre },
    post: {
      data: dataPostGenre,
      status: statusPostGenre,
      error: errorPostGenre,
    },
  } = useSelector(({ common }) => common);

  const dispatch = useDispatch();
  const [isReadOnly] = useState(
    hasAccess({ type: "name", resource: "EDIT USERS" }) ? false : true,
  );
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [formValues, setFormValues] = useState({
    title: selectedGenre?.TITLE,
    coverImage: selectedGenre?.COVER_IMAGE,
    status: selectedGenre?.STATUS,
  });
  const [errors, setErrors] = useState([]);

  const hasChanges = useMemo(() => {
    if (!selectedGenre) return true;
    const initialPayload = {
      TITLE: selectedGenre.TITLE || "",
      STATUS: selectedGenre.STATUS || "A",
    };
    const currentPayload = {
      TITLE: formValues.title || "",
      STATUS: formValues.status || "A",
    };
    const diff = getObjectDifference(initialPayload, currentPayload);
    return Object.keys(diff).length > 0 || Boolean(formValues.document);
  }, [formValues, selectedGenre]);
  const statuses = [
    {
      value: "A",
      label: "Active",
    },
    {
      value: "I",
      label: "Inactive",
    },
    {
      value: "D",
      label: "Disabled",
    },
  ];

  const validateSchema = {
    title: {
      presence: { allowEmpty: false },
    },
  };

  //* use effects

  useEffect(() => {
    if (dataPutGenre || dataPostGenre) {
      dispatch(
        showSnackbar({
          open: true,
          type: "success",
          message: `Genre ${
            dataPutGenre ? "updated" : "registered"
          } successfully.`,
          timeout: null,
        }),
      );
      setShouldRefresh(true);
      dispatch(resetStateCommon());
      handleClose();
    }
  }, [dataPutGenre, dataPostGenre]);

  useEffect(() => {
    if (errorPutGenre || errorPostGenre) {
      dispatch(
        showSnackbar({
          open: true,
          type: "error",
          message:
            errorPutGenre?.message ||
            errorPostGenre?.message ||
            "An error occurred",
        }),
      );
    }
  }, [errorPutGenre, errorPostGenre]);

  //* methods
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    let inputValue = value;

    if (files?.length) {
      if (files[0]?.size > 5000000) {
        dispatch(
          showSnackbar({
            open: true,
            type: "error",
            message: "Upload should not exceed 5 megabytes in size",
          }),
        );
        return;
      }
      inputValue = files[0];
    }

    setFormValues((prevState) => ({ ...prevState, [name]: inputValue }));
  };

  const handleSubmit = async (e) => {
    if (selectedGenre?.STATUS === "D") {
      dispatch(
        showSnackbar({
          open: true,
          message: "Genre is disabled. Update is not allowed",
          timeout: 3000,
          type: "error",
        }),
      );
      return;
    } else {
      if (!hasChanges) {
        dispatch(
          showSnackbar({
            open: true,
            message: "No changes to submit",
            timeout: 3000,
            type: "info",
          }),
        );
      } else {
        e?.preventDefault();

        let _errors = validate(formValues, validateSchema);

        if (_errors) {
          setErrors(_errors);
        } else {
          setErrors([]);

          let _uploadPayload = {};

          _uploadPayload = {
            TITLE: formValues?.title,
            STATUS: formValues?.status,
          };

          if (formValues.document) {
            _uploadPayload.COVER_IMAGE = await getBase64(formValues.document);
          }

          if (selectedGenre) {
            const initialPayload = {
              TITLE: selectedGenre.TITLE || "",
              STATUS: selectedGenre.STATUS || "A",
            };
            const diff = getObjectDifference(initialPayload, _uploadPayload);
            if (formValues.document) {
              diff.COVER_IMAGE = await getBase64(formValues.document);
            }

            dispatch(
              updateCommonResource({
                resource: "genres",
                id: selectedGenre?.GENRE_ID,
                payload: diff,
              }),
            );
          } else {
            _uploadPayload.resource = "genres";
            dispatch(addCommonResource(_uploadPayload));
          }
        }
      }
    }
  };

  //* builders
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={() => handleClose(shouldRefresh)}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {selectedGenre ? "Update" : "Register"} Genre
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              name="title"
              label="Title"
              value={formValues.title || ""}
              onChange={handleChange}
              error={Boolean(errors.title)}
              helperText={errors.title?.[0] || ""}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              margin="dense"
              variant="outlined"
              name="status"
              label="Genre Status"
              value={formValues.status || ""}
              onChange={handleChange}
            >
              {statuses.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={12}>
            <FileInput
              onChange={handleChange}
              file={formValues.document}
              fileType="image/*"
              label="Cover Image"
              name="document"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isReadOnly && !shouldRefresh && (
          <Button
            variant="contained"
            disabled={
              statusPutGenre === "loading" || statusPostGenre === "loading" || !hasChanges
            }
            onClick={handleSubmit}
          >
            {(statusPutGenre === "loading" ||
              statusPostGenre === "loading") && (
              <CircularProgress size={18} sx={{ mr: 1 }} />
            )}
            {selectedGenre ? "Update" : "Register"}
          </Button>
        )}

        <Button
          variant="outlined"
          color="secondary"
          disabled={
            statusPutGenre === "loading" || statusPostGenre === "loading"
          }
          onClick={(e) => {
            e.preventDefault();
            handleClose(shouldRefresh);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateGenreDialog;
